import { Map } from "maplibre-gl";
import { BaseLayerLayerTypes } from "./baselayers/BaseLayerLayerTypes";
/**
 * A wrapper around the Map object to manager layers and interactions which makes working with MapBox easier.
 */
export class MapManager {
    constructor(options) {
        this.customLayers = [];
        this.initializedListeners = [];
        this.imageryVisible = false;
        this.isLoaded = false;
        this.owner = "";
        this.baseLayer = options.baseLayer;
        this.imageryLayers = options.imageryLayers;
        this.customSource = options.customSource;
    }
    requestOwnership(key) {
        if (this.owner.length === 0) {
            this.owner = key;
            return true;
        }
        return this.owner === key;
    }
    releaseOwnership(key) {
        if (this.owner === key) {
            this.owner = "";
        }
    }
    /**
     * @see Map.getContainer
     */
    getContainer() {
        return this.map.getContainer();
    }
    /**
     * Creates the map and attaches it to the specified element.
     * @returns The MapLibre-map object
     */
    async initialize(attachTo, afterLoad, options) {
        // apply the base layer stylesheet.
        this.baseLayerStyle = await this.baseLayer.buildCustomizedStyle(this.customSource);
        const style = this.baseLayerStyle.getEmptyStyle();
        // add imagery layers.
        this.imageryLayers.forEach(imageryLayer => {
            style.sources[imageryLayer.layer.source] = imageryLayer.source;
            style.layers.push(imageryLayer.layer);
        });
        // add base layer layers.
        this.baseLayerStyle.getLayers().forEach(l => {
            style.layers.push(l.layer);
        });
        let container;
        if (typeof attachTo === "string") {
            container = document.getElementById(attachTo);
            if (container === undefined) {
                throw new Error("Error: trying to attach the map element, but the HTML-page does not have an element with id " + attachTo + ". Check for typos and that the script is included _after_ the elements declaration");
            }
        }
        else {
            container = attachTo;
            if (container === undefined) {
                throw new Error("Error: can not attach the map to undefined, make sure you pass in a correct HTML-element or element ID");
            }
        }
        // create the map.
        const mapOptions = { container, style, ...options };
        mapOptions.maxZoom = 19;
        mapOptions.doubleClickZoom = false;
        this.map = new Map(mapOptions);
        this.map.on("load", async (e) => {
            this.isLoaded = true;
            await afterLoad(e, this.map);
        });
        this.sendInitializedSignal();
        return this.map;
    }
    /**
     * Updates the custom source and the stylesheet to match.
     *
     * @param customSource The custom source settings.
     */
    async updateCustomSource(customSource) {
        const previousSourceId = this.customSource.sourceId;
        // set the new source.
        this.customSource.source = customSource.source;
        this.customSource.sourceId = customSource.sourceId;
        // get all layers with the old source id.
        const layers = [];
        const currentLayers = this.map.getStyle().layers;
        currentLayers.forEach((l, i) => {
            if (l["source"] !== previousSourceId)
                return;
            // find a next layer with a different source as 'before', if any.
            // otherwise we won't be able to add layer with a before that has been removed.
            let before = undefined;
            for (let next = i + 1; next < currentLayers.length; next++) {
                const nextLayer = currentLayers[next];
                if (nextLayer["source"] === previousSourceId)
                    continue;
                before = nextLayer.id;
                break;
            }
            // save layer, we will remove it layer.
            layers.push({ layer: l, before: before });
        });
        // remove all layers with the old source id.
        currentLayers.forEach((l, i) => {
            if (l["source"] !== previousSourceId)
                return;
            this.map.removeLayer(l.id);
        });
        // remove old source.
        this.map.removeSource(previousSourceId);
        // add new source.
        this.map.addSource(this.customSource.sourceId, this.customSource.source);
        // add layers again, with the updated source.
        layers.forEach(l => {
            l.layer["source"] = this.customSource.sourceId;
            this.map.addLayer(l.layer, l.before);
        });
    }
    /**
     * Hooks up a listener.
     * @param event
     * @param e
     */
    on(event, e) {
        if (event == "initialized") {
            this.initializedListeners.push(e);
            return;
        }
        this.map.on(event, e);
    }
    /**
     * @see Map.project
     */
    project(lnglat) {
        return this.map.project(lnglat);
    }
    /**
     * @see Map.unproject
     */
    unproject(point) {
        return this.map.unproject(point);
    }
    /**
     * Hooks up a listener.
     * @see Map.once
     */
    once(event, e) {
        this.map.once(event, e);
    }
    /**
     * Sets a layers' filter.
     * @see Map.setFilter
     */
    setFilter(layer, filter, options) {
        this.map.setFilter(layer, filter, options);
    }
    /**
     * Hooks up a listener.
     * @see Map.onLayer
     */
    onLayer(event, layer, e) {
        this.map.on(event, layer, e);
    }
    /**
     * Add a control element to the map
     * @see Map.addControl
     */
    addControl(control, position) {
        this.map.addControl(control, position);
    }
    /**
     * Checks if the map has loaded.
     * @returns True if the map was created and loaded.
     */
    loaded() {
        if (typeof this.map === "undefined")
            return false;
        return this.isLoaded;
    }
    /**
     * If the surrounding HTML changed, calling this method will resize the map to match the surroundings again
     * @see Map.resize
     */
    resize() {
        // if (!this.loaded()) throw new Error("map has not loaded yet");
        this.map.resize();
    }
    /**
     * Sets the map view, thus which area is visible.
     * @param zoom The zoom level.
     * @param center The center.
     */
    setView(zoom, center) {
        // if (!this.loaded()) throw new Error("map has not loaded yet");
        this.map.setCenter(center);
        this.map.setZoom(zoom);
    }
    /**
     * Gets the map view.
     * @returns The map view.
     */
    getView() {
        const center = this.map.getCenter();
        return {
            zoom: this.map.getZoom(),
            center: center
        };
    }
    /**
     * Fits the map to the given bounds.
     */
    fitBounds(bounds, options, eventData) {
        this.map.fitBounds(bounds, options, eventData);
    }
    /**
     * Recreate the stylesheet to update the base layer.
     */
    async toggleBaseLayer(baseLayer) {
        if (this.baseLayer === baseLayer)
            return;
        // remove all old baselayer layers but keep imagery layers.
        this.baseLayerStyle.getLayers().forEach(l => {
            this.map.removeLayer(l.layer.id);
        });
        // get a customized style from the baselayer.
        const newStyle = await baseLayer.buildCustomizedStyle(this.customSource);
        // get the lowest custom layer for each position.
        const customLayersAt = {};
        this.customLayers.forEach(l => {
            const type = l.isBelow;
            const currentPosition = customLayersAt[type];
            if (typeof currentPosition !== "undefined")
                return;
            customLayersAt[type] = l.id;
        });
        //console.log(customLayersAt);
        // add the new style layers in the correct position
        // relative to the custom layers already there.
        const layers = newStyle.getLayers();
        layers.forEach(l => {
            let behind = undefined;
            if (typeof l.isBehind !== "undefined") {
                const i = BaseLayerLayerTypes.indexOf(l.isBehind);
                if (i >= 0) {
                    for (let t = i; t < BaseLayerLayerTypes.length; t++) {
                        behind = customLayersAt[BaseLayerLayerTypes[t]];
                        if (typeof behind !== "undefined")
                            break;
                    }
                }
            }
            //console.log(`${l.layer.id} is behind ${behind}`);
            this.map.addLayer(l.layer, behind);
        });
        // update base layer state.
        this.baseLayer = baseLayer;
        this.baseLayerStyle = newStyle;
        // toggle imagery again.
        this.toggleImagery(this.imageryVisible);
    }
    /**
     * Toggles the imagery visibility.
     * @param visible cThe visible flag.
     */
    toggleImagery(visible) {
        this.imageryVisible = visible;
        if (this.imageryVisible) {
            // make fills visible.
            this._setFillVisibility(false);
            //this.setRoadColor(false);
            //this.setLabels(false);
            // show imagery.
            this.imageryLayers.forEach(i => {
                this.map.setLayoutProperty(i.layer.id, "visibility", "visible");
            });
            // hide background.
            this.map.setLayoutProperty("background", "visibility", "none");
        }
        else {
            // make fills visible.
            this._setFillVisibility(true);
            //this.setRoadColor(false);
            //this.setLabels(false);
            // show imagery.
            this.imageryLayers.forEach(i => {
                this.map.setLayoutProperty(i.layer.id, "visibility", "none");
            });
            // hide background.
            this.map.setLayoutProperty("background", "visibility", "visible");
        }
    }
    addMarker(marker) {
        marker.addTo(this.map);
    }
    addSource(id, source) {
        // if (!this.loaded()) throw new Error("map has not loaded yet");
        this.map.addSource(id, source);
    }
    addOrGetSource(id, source) {
        // if (!this.loaded()) throw new Error("map has not loaded yet");
        const e = this.map.getSource(id);
        if (typeof e !== "undefined")
            return e;
        this.map.addSource(id, source);
        return this.map.getSource(id);
    }
    /**
     * Removes the source if it exists.
     *
     * @return true if the source was removed, false if it didn't exist in the first place
     */
    removeSource(id) {
        if (this.map.getSource(id) !== undefined) {
            this.map.removeSource(id);
            return true;
        }
        return false;
    }
    getSource(id) {
        return this.map.getSource(id);
    }
    hasSource(id) {
        return typeof this.map.getSource(id) !== "undefined";
    }
    /**
     * Adds a custom layer.
     * @param layer The layer.
     * @param behind The behind parameter, to insert the layer behind other layers.
     */
    addLayer(layer, position) {
        // if (!this.loaded()) throw new Error("map has not loaded yet");
        this.customLayers.push({ id: layer.id, isBelow: position });
        this.map.addLayer(layer, this.baseLayerStyle.getLowestLayerOfType(position));
    }
    setPaintProperty(layer, name, value) {
        this.map.setPaintProperty(layer, name, value);
    }
    setLayoutProperty(layer, name, value) {
        this.map.setLayoutProperty(layer, name, value);
    }
    removeLayer(layerId) {
        const index = this.customLayers.findIndex(l => l.id === layerId);
        if (index < 0) {
            return; // Not in the list 
        }
        this.customLayers.splice(index, 1);
        this.map.removeLayer(layerId);
    }
    hasLayer(id) {
        return typeof this.map.getLayer(id) !== "undefined";
    }
    /**
     * Hides a layer.
     * @param id The layer id.
     */
    hideLayer(id) {
        this.map.setLayoutProperty(id, "visibility", "none");
    }
    /**
     * Shows a layer.
     * @param id The layer id.
     */
    showLayer(id) {
        this.map.setLayoutProperty(id, "visibility", "visible");
    }
    isLayerVisible(id) {
        return this.map.getLayoutProperty(id, "visibility") !== "none";
    }
    setCursor(cursor) {
        const canvas = this.map.getCanvasContainer();
        canvas.style.cursor = cursor;
    }
    isStyleLoaded() {
        return this.map.isStyleLoaded();
    }
    queryRenderedFeatures(geometryOrOptions, options) {
        return this.map.queryRenderedFeatures(geometryOrOptions, options);
    }
    getBounds() {
        return this.map.getBounds();
    }
    zoomIn() {
        this.map.zoomIn();
        return this;
    }
    zoomOut() {
        this.map.zoomOut();
        return this;
    }
    setZoom(zoom) {
        this.map.setZoom(zoom);
        return this;
    }
    /**
     * Execute all functions in the 'initializedListeners'-list
     */
    sendInitializedSignal() {
        this.initializedListeners.forEach(e => {
            e({});
        });
    }
    _setFillVisibility(visible) {
        const style = this.map.getStyle();
        for (let l = 0; l < style.layers.length; l++) {
            const layer = style.layers[l];
            if (layer.type === "fill") {
                if (!layer.layout) {
                    layer.layout = {};
                }
                if (visible) {
                    this.map.setLayoutProperty(layer.id, "visibility", "visible");
                }
                else {
                    this.map.setLayoutProperty(layer.id, "visibility", "none");
                }
            }
        }
    }
}
